import {
  DOMAIN_EVENT_FEED_LOOKBACK_MS,
  DOMAIN_EVENT_LOG_MAX_ROWS,
  domainEventLogTrimCount,
  domainEventsApi,
  isLaterSeq,
  matchesFeedView,
  staleHeadCount,
  type DomainEventFrame,
} from '#entities';
import { advanceSeq, resourceUpdated } from '#shared';

import type { AppDispatch, RootState } from '#shared';

/**
 * How many rows may wait outside the 48 h window, or past the ceiling, before
 * one frame trims them all. A one-row `remove` per frame shifts every row of
 * the immer draft; batching turns that into one whole-log replace per 500 rows.
 * Keep it above the grouper's `MAX_INCREMENTAL_CHANGES`: only a trim that
 * forces a full walk resets its partitions.
 */
const EVICTION_SLACK = 500;

type CachePatch = Parameters<
  typeof domainEventsApi.util.patchQueryData
>[2][number];

/** First index whose id is not lower than `id`, in a log ascending by id. */
const insertionIndex = (
  events: readonly DomainEventFrame[],
  id: string,
): number => {
  let low = 0;
  let high = events.length;

  while (low < high) {
    const mid = (low + high) >>> 1;

    if (isLaterSeq(id, events[mid].id)) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
};

/**
 * Apply one live/catch-up `domainEvent` frame into the rolling `getDomainEvents`
 * cache: idempotent by `id`, kept ascending by numeric `id`. Also advances the
 * session seq cursor whenever the cache entry exists (see below).
 *
 * `patchQueryData` is a no-op if the `getDomainEvents` cache entry doesn't
 * exist yet. `EventsFeedProvider` holds a durable `useDomainEventFeedQuery()`
 * subscription, and the cold-start `getDomainEvents.initiate` in
 * `domainEventCatchup.ts` creates the entry — but a live frame can still
 * arrive in the window before either has populated it. Such a frame would be
 * silently dropped from the cache while still advancing the cursor past it —
 * poisoning the cursor and losing the frame for good. So this thunk checks the
 * cache entry first: if it's missing, the frame is dropped WITHOUT advancing
 * the cursor, leaving the cold-start (or a later since-seq catch-up) able to
 * recover it.
 *
 * When a target entry DOES exist, `advanceSeq` runs unconditionally — including
 * on the idempotent-dedup branch where the frame was already present and nothing
 * was written, and on the branch where the frame does not match a view's filter.
 * That is safe only because `advanceSeq` is itself monotonic (it never moves the
 * cursor backwards), so re-advancing to an already-seen id is a harmless no-op
 * rather than a regression. It is also NECESSARY for the filtered case: a
 * `view=case` session sees mostly non-matching frames, and holding the cursor
 * back on each of them would stall catch-up permanently.
 *
 * TMDXNC-54: the cache is now keyed by relevance view, so this patches EVERY
 * cached arg rather than the historical hardcoded `undefined`. A frame is only
 * written into entries whose view accepts it (`matchesFeedView`) — otherwise a
 * filtered entry would refill over the socket with exactly the rows the filter
 * exists to exclude. Each entry keeps the 48 h window by `ts` and at most
 * `DOMAIN_EVENT_LOG_MAX_ROWS` rows. Rows past either bound wait until more than
 * `EVICTION_SLACK` have gathered, then one frame drops them in one replace.
 */
export const applyDomainEventFrame =
  (frame: DomainEventFrame) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const state = getState();
    const cachedArgs = domainEventsApi.util
      .selectCachedArgsForQuery(state, 'getDomainEvents')
      .filter(
        arg =>
          domainEventsApi.endpoints.getDomainEvents.select(arg)(state).data !==
          undefined,
      );
    if (cachedArgs.length === 0) {
      // No populated cache entry yet (a live frame arriving before the lookback
      // seed). Drop it WITHOUT advancing the cursor, so cold-start still runs
      // and a later since-seq catch-up can recover it. Advancing here would
      // poison the cursor and could blank the session.
      return;
    }
    const receivedAtMs = Date.now();
    for (const arg of cachedArgs) {
      if (!matchesFeedView(frame, arg.view)) {
        continue;
      }
      const log =
        domainEventsApi.endpoints.getDomainEvents.select(arg)(state).data;
      if (log === undefined) {
        continue; // unreachable: `cachedArgs` holds only populated entries
      }
      const at = insertionIndex(log.events, frame.id);
      if (log.events[at]?.id === frame.id) {
        continue;
      }
      const cutoffMs = receivedAtMs - DOMAIN_EVENT_FEED_LOOKBACK_MS;
      const trimDue =
        log.events.length + 1 > DOMAIN_EVENT_LOG_MAX_ROWS + EVICTION_SLACK ||
        staleHeadCount(log.events, cutoffMs) > EVICTION_SLACK;
      const row = { ...frame, receivedAtMs };
      // Hand-written patches, not `updateQueryData`: RTK applies a recipe's
      // patches with immer's `applyPatches`, which deep-clones every patch
      // value, and a recipe that reassigns or shifts the array emits every
      // row as a value. An `add` clones only the new row, so every kept row
      // keeps its identity. The trimming frame swaps the log in one O(n)
      // replace instead, and gives up identity for that frame.
      const inserted = trimDue
        ? [...log.events.slice(0, at), row, ...log.events.slice(at)]
        : null;
      const patches: CachePatch[] = inserted
        ? [
            {
              op: 'replace',
              path: ['events'],
              value: inserted.slice(
                domainEventLogTrimCount(inserted, receivedAtMs),
              ),
            },
          ]
        : [{ op: 'add', path: ['events', at], value: row }];
      if (isLaterSeq(frame.id, log.latestSeq)) {
        patches.push({ op: 'replace', path: ['latestSeq'], value: frame.id });
      }
      dispatch(
        domainEventsApi.util.patchQueryData('getDomainEvents', arg, patches),
      );
    }
    // One stamp per resource, not per cache entry: the loop above can patch
    // several view-scoped cache args for the same frame, and a per-iteration
    // dispatch would write the identical timestamp N times.
    dispatch(resourceUpdated({ resource: 'domainEvents', atMs: Date.now() }));
    advanceSeq(frame.id);
  };
